<template>
  <div class="fleet">
    <div class="fleet-topline">
      <h3 class="fleet-title">{{ t('message.rtu.fleetTitle') }}</h3>
      <span class="updated">{{ t('message.rtu.lastUpdated') }} {{ lastUpdated }}</span>
      <el-tag v-if="isDemo" type="warning" size="small" effect="plain">DEMO</el-tag>
      <span style="flex:1" />
      <el-button size="small" :icon="Share" @click="editGroups">{{ t('message.rtu.groupEditTitle') }}</el-button>
    </div>
    <rtu-group-editor ref="groupEditor" @saved="reloadGroups" />

    <div v-loading="loading && !fleet.length" class="sections">
      <collapsible-card
        v-for="section in sections"
        :key="section.key"
        :title="section.label"
        :hint="t('message.rtu.groupCount', { n: section.devices.length })"
      >
        <el-table :data="section.devices" row-key="unitId" :row-class-name="rowClass"
          @row-click="open" class="fleet-table" :show-header="true">
          <el-table-column :label="t('message.rtu.fleetName')" min-width="180">
            <template #default="{ row }">
              <span class="rtu-name" :title="row.dtuName || ''">{{ row.name }}</span>
              <el-tag v-if="row.groupInfo" size="small" effect="plain" class="offset-pill">
                {{ row.groupInfo.offset >= 0 ? '+' : '' }}{{ row.groupInfo.offset }}°
              </el-tag>
              <el-tag v-if="alarmCount(row)" type="danger" size="small" class="alarm-pill">
                {{ t('message.rtu.alarmBanner', { n: alarmCount(row) }) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column :label="t('message.rtu.opMode')" min-width="150">
            <template #default="{ row }">
              <span v-if="row.hasData" class="mode-chip" :class="modeClass(row)">{{ modeText(row) }}</span>
              <span v-else class="muted">{{ t('message.rtu.fleetNoData') }}</span>
            </template>
          </el-table-column>

          <el-table-column :label="t('message.rtu.roomTemp')" min-width="110" align="right">
            <template #default="{ row }">
              <span v-if="row.hasData" class="num">{{ fmt(row.values.room_temp, 1) }}<small>°F</small></span>
              <span v-else class="muted">—</span>
            </template>
          </el-table-column>

          <el-table-column :label="t('message.rtu.fleetSetpoints')" min-width="120" align="right">
            <template #default="{ row }">
              <span v-if="row.hasData" class="num sp" :title="t('message.rtu.fleetSetpointsTip')">
                {{ fmt(activeSp(row, 'heat'), 1) }} / {{ fmt(activeSp(row, 'cool'), 1) }}<small>°F</small>
                <small class="period">{{ Number(row.values.occupied) ? t('message.rtu.spOcc') : t('message.rtu.spUnocc') }}</small>
              </span>
              <span v-else class="muted">—</span>
            </template>
          </el-table-column>

          <el-table-column :label="t('message.rtu.fleetCompressor')" min-width="110">
            <template #default="{ row }">
              <span v-if="row.hasData" class="state" :class="{ on: row.values.y1 || row.values.y2 }">{{ compText(row) }}</span>
              <span v-else class="muted">—</span>
            </template>
          </el-table-column>

          <el-table-column :label="t('message.rtu.fleetHeater')" min-width="110">
            <template #default="{ row }">
              <span v-if="row.hasData && row.hasHeater" class="state" :class="{ on: row.values.w1 || row.values.w2 }">{{ heatText(row) }}</span>
              <span v-else class="muted">—</span>
            </template>
          </el-table-column>

          <el-table-column width="44">
            <template #default><el-icon class="chevron"><ArrowRight /></el-icon></template>
          </el-table-column>
        </el-table>
      </collapsible-card>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ArrowRight, Share } from '@element-plus/icons-vue';
import { getRtuAdapter, isDemoMode } from '@/services/rtuAdapter';
import { getGroupConfig } from '@/services/rtuGroups';
import RtuGroupEditor from './RtuGroupEditor.vue';
import CollapsibleCard from './CollapsibleCard.vue';

const { t } = useI18n();
const router = useRouter();
const adapter = getRtuAdapter();
const isDemo = isDemoMode();

const fleet = ref([]);
const groupCfg = ref({});
const loading = ref(false);
const lastUpdated = ref('—');
const groupEditor = ref(null);
const POLL_MS = 10000;
let timer = null;

// 分组分节：有组的按组号排序，未分组殿后
const sections = computed(() => {
  const byGroup = new Map();
  const ungrouped = [];
  fleet.value.forEach((d) => {
    const cfg = groupCfg.value[d.unitId];
    const row = { ...d, groupInfo: cfg?.group ? cfg : null };
    if (cfg?.group) {
      if (!byGroup.has(cfg.group)) byGroup.set(cfg.group, []);
      byGroup.get(cfg.group).push(row);
    } else {
      ungrouped.push(row);
    }
  });
  const out = [...byGroup.keys()].sort((a, b) => a - b).map((g) => ({
    key: `group-${g}`,
    label: t('message.rtu.groupLabel', { g }),
    devices: byGroup.get(g),
  }));
  if (ungrouped.length) {
    out.push({ key: 'ungrouped', label: t('message.rtu.ungroupedLabel'), devices: ungrouped });
  }
  return out;
});

function editGroups() {
  groupEditor.value?.openWith(fleet.value.map((d) => ({ unitId: d.unitId, name: d.name })));
}

async function reloadGroups() {
  groupCfg.value = await getGroupConfig();
  refresh();
}

const fmt = (v, dp) => (v == null ? '—' : Number(v).toFixed(dp));
const alarmCount = (d) => Object.values(d.health.status).filter((v) => v === 'alarm').length;
const modeText = (d) => {
  const m = d.values.op_mode;
  return m === undefined || m === null ? '—' : t(`message.rtu.modes.${Number(m)}`);
};
// OMC 枚举：1 消防锁定, 2 关, 3 通风, 4 制热, 5 制冷
const MODE_CLASSES = ['off', 'fire', 'off', 'vent', 'heat', 'cool'];
const modeClass = (d) => MODE_CLASSES[Number(d.values.op_mode)] || 'off';
const stageText = (s1, s2) => (s2 ? t('message.rtu.stage2') : s1 ? t('message.rtu.stage1') : t('message.rtu.off'));
const compText = (d) => stageText(Number(d.values.y1), Number(d.values.y2));
const heatText = (d) => stageText(Number(d.values.w1), Number(d.values.w2));
const rowClass = ({ row }) => (alarmCount(row) ? 'alarmed-row' : (!row.hasData ? 'offline-row' : ''));
// 该机组当前时段（有人/无人）的实际设定值 —— 分组时可直观看到各机组的错峰值
const activeSp = (row, kind) =>
  Number(row.values.occupied) ? row.values[`sp_occ_${kind}`] : row.values[`sp_unocc_${kind}`];

function open(row) {
  router.push({ path: '/clientMain/rtuStatus', query: { unit: row.unitId } });
}

async function refresh() {
  loading.value = true;
  try {
    fleet.value = await adapter.getFleet();
    lastUpdated.value = new Date().toLocaleTimeString();
  } catch (e) {
    console.error('[rtu] fleet load failed:', e);
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  groupCfg.value = await getGroupConfig().catch(() => ({}));
  refresh();
  timer = setInterval(refresh, POLL_MS);
});
onBeforeUnmount(() => { if (timer) clearInterval(timer); });
</script>

<style scoped>
.fleet { padding: 4px 4px 32px; max-width: 1180px; }
.fleet-topline { display: flex; align-items: center; gap: 12px; padding: 4px 0 12px; }
.fleet-title { margin: 0; font-size: 16px; font-weight: 600; }
.updated { font-size: 12px; color: var(--el-text-color-secondary); }
.sections { display: flex; flex-direction: column; gap: 14px; min-height: 100px; }

.fleet-table :deep(tbody tr) { cursor: pointer; }
.fleet-table :deep(.alarmed-row td:first-child) { box-shadow: inset 3px 0 0 var(--el-color-danger); }
.fleet-table :deep(.offline-row) { opacity: 0.65; }

.rtu-name { font-weight: 600; }
.offset-pill { margin-left: 8px; }
.alarm-pill { margin-left: 8px; }
.num { font-variant-numeric: tabular-nums; font-weight: 600; font-size: 15px; }
.num.sp { font-size: 13.5px; font-weight: 500; color: var(--el-text-color-regular); }
.num.sp .period {
  margin-left: 5px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em;
  color: var(--el-text-color-secondary); background: var(--el-fill-color); border-radius: 3px; padding: 1px 4px;
}
.num small { font-size: 11px; font-weight: 400; color: var(--el-text-color-secondary); margin-left: 1px; }
.state { font-size: 13px; color: var(--el-text-color-secondary); }
.state.on { color: var(--el-color-success); font-weight: 600; }
.muted { color: var(--el-text-color-placeholder); }
.chevron { color: var(--el-text-color-placeholder); }

.mode-chip {
  font-size: 12.5px; font-weight: 600; border-radius: 999px; padding: 3px 12px;
  background: var(--el-fill-color); color: var(--el-text-color-regular); white-space: nowrap;
}
.mode-chip.cool { background: var(--el-color-primary-light-8); color: var(--el-color-primary); }
.mode-chip.heat { background: var(--el-color-danger-light-8, #fde2e2); color: var(--el-color-danger); }
.mode-chip.vent { background: var(--el-color-success-light-8, #e1f3d8); color: var(--el-color-success); }
.mode-chip.econ { background: var(--el-color-warning-light-8, #faecd8); color: var(--el-color-warning); }
.mode-chip.fire { background: var(--el-color-danger); color: #fff; }
</style>
